<template>
  <div class="modal-overlay" @click="$emit('closeModal')">
    <div class="modal-card" @click.stop>
      <div class="modal-header">
        <div class="modal-title-row">
          <div class="modal-device">{{ device }}</div>

          <div v-if="lastModified" class="modal-timestamp">
            <span class="modal-timestamp__label">Captured</span>
            <span class="modal-timestamp__value">
              {{ formattedLastModified }}
            </span>
            <span v-if="relativeLastModified" class="modal-timestamp__relative">
              ({{ relativeLastModified }})
            </span>
          </div>

          <div v-else class="modal-timestamp modal-timestamp--muted">
            Capture time unavailable
          </div>
        </div>
      </div>

      <div class="modal-image-wrap">
        <img
          class="modal-image"
          :src="imageSrc"
          :alt="device"
          @load="fetchImageMetadata"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    device: {
      type: String,
      default: "",
    },
    triggerRefresh: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      snitchDomain: process.env.VUE_APP_SNITCH_SERVER,
      lastModified: "",
      nowTick: Date.now(),
      nowInterval: null,
    };
  },

  computed: {
    imageUrl() {
      return `${this.snitchDomain}/captures/${this.device}-latest.jpg`;
    },

    imageSrc() {
      return `${this.imageUrl}?${this.triggerRefresh}`;
    },

    parsedLastModified() {
      if (!this.lastModified) return null;
      const date = new Date(this.lastModified);
      return Number.isNaN(date.getTime()) ? null : date;
    },

    formattedLastModified() {
      if (!this.parsedLastModified) return "";

      return this.parsedLastModified.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
      });
    },

    relativeLastModified() {
      if (!this.parsedLastModified) return "";

      const diffMs = this.nowTick - this.parsedLastModified.getTime();
      const diffSeconds = Math.max(0, Math.floor(diffMs / 1000));

      if (diffSeconds < 10) return "just now";
      if (diffSeconds < 60) return `${diffSeconds}s ago`;

      const diffMinutes = Math.floor(diffSeconds / 60);
      if (diffMinutes < 60) return `${diffMinutes}m ago`;

      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours < 24) return `${diffHours}h ago`;

      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    },
  },

  watch: {
    device: {
      immediate: true,
      handler() {
        this.lastModified = "";
        this.fetchImageMetadata();
      },
    },

    triggerRefresh() {
      this.fetchImageMetadata();
    },
  },

  mounted() {
    this.nowInterval = setInterval(() => {
      this.nowTick = Date.now();
    }, 1000);
  },

  beforeDestroy() {
    if (this.nowInterval) clearInterval(this.nowInterval);
  },

  methods: {
    async fetchImageMetadata() {
      if (!this.device) return;

      try {
        const response = await axios.head(this.imageUrl, {
          params: { t: this.triggerRefresh },
        });

        this.lastModified = response.headers["last-modified"] || "";
      } catch (error) {
        this.lastModified = "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px;
  background: rgba(0, 0, 0, 0.9);
}

.modal-card {
  width: min(1700px, 100%);
  max-width: 100%;
  height: min(96vh, 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  background: #e5e7eb;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
}

.modal-header {
  flex: 0 0 auto;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  background: #e5e7eb;
}

.modal-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.modal-device {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
}

.modal-timestamp {
  font-size: 0.92rem;
  color: #334155;
}

.modal-timestamp__label {
  font-weight: 600;
  margin-right: 6px;
}

.modal-timestamp__value {
  color: #1e293b;
}

.modal-timestamp__relative {
  margin-left: 6px;
  color: #64748b;
}

.modal-timestamp--muted {
  color: #64748b;
}

.modal-image-wrap {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: #111827;
}

.modal-image {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 12px;
  }

  .modal-card {
    height: min(96vh, 100%);
  }

  .modal-header {
    padding: 14px 16px;
  }
}
</style>
